/**
 * This custom hook should allow components access to the current customer data
 * when it has been loaded.
 *
 * The customer data state is inherited by the customer form state.
 */
import { useState } from "react";

export interface CustomerDataAddress {
  /** Street address line 1 for contact address. */
  streetAddress1: string | null | undefined;
  /** Street address line 2 for contact address. */
  streetAddress2: string | null | undefined;
  /** Street address line 3 for contact address. */
  streetAddress3: string | null | undefined;
  /** Locality/city for contact address. */
  addressLocality: string | null | undefined;
  /** Region for contact address. */
  addressRegion: string | null | undefined;
  /** Postal code for contact address. */
  postalCode: string | null | undefined;
  /** Country for contact address. */
  addressCountry: string | null | undefined;
}

export interface CustomerDataStateObject extends CustomerDataAddress {
  [propName: string]: any;
  /** is remote - created by sales advisor */
  isRemote: boolean | null | undefined;
  /** The customer's identifier. */
  identifier: string | number | null | undefined;
  /** The customer's gender. */
  gender: string | null | undefined;
  /** The customer's honorific prefix/title. */
  honorificPrefix: string | null | undefined;
  /** The customer's alternate given/first name. Could be a local/global/translated version. */
  givenName: string | null | undefined;
  /** The customer's alternate given/first name #1. Could be a local/global/translated version. */
  alternateGivenName1: string | null | undefined;
  /** The customer's alternate given/first name #2. Could be a local/global/translated version. */
  alternateGivenName2: string | null | undefined;
  /** The customer's family name/last name/surname. */
  familyName: string | null | undefined;
  /** The customer's alternate family name #1. Could be a local/global/translated version. */
  alternateFamilyName1: string | null | undefined;
  /** The customer's alternate family name #2. Could be a local/global/translated version. */
  alternateFamilyName2: string | null | undefined;
  /** The customer's birth date. */
  birthDate: string | null | undefined;
  /** The nationality of the customer (most likely a country code). */
  nationality: string | null | undefined;
  /** Customer's contact email address. */
  email: string | null | undefined;
  /** Customer's contact telephone number. */
  telephone: string | null | undefined;
  /** Customer's contact mobile phone number. */
  mobileTelephone: string | null | undefined;
  /** Customer's Wechat ID (social media app). */
  wechatId?: string | null | undefined;
  /** Customer's Line ID (social media app). */
  lineId?: string | null | undefined;
  /** Customer opted in to received communications by apps/messaging services. */
  appsConsent: boolean | null | undefined;
  /** Customer opted in to received communications by email. */
  emailConsent: boolean | null | undefined;
  /** Customer opted in to received communications by post. */
  postConsent: boolean | null | undefined;
  /** Customer opted in to received communications by phone. */
  phoneConsent: boolean | null | undefined;
  /** The client adviser's ID. */
  salesPerson: string | number | null | undefined;
  /** The store ID. */
  creationStore: string | number | null | undefined;
  /** The customer's preferred language. */
  preferredLanguage: string | null | undefined;

  isEmailValid?: boolean | null;
  isAddressValid?: boolean | null;
  isMobilePhoneValid?: boolean | null;
}

export interface ExistingCustomerDataStateObject
  extends CustomerDataStateObject {
  identifier: string | undefined;
}

export type SetCustomerDataState = React.Dispatch<
  React.SetStateAction<CustomerDataStateObject>
>;

export type CustomerDataState = [CustomerDataStateObject, SetCustomerDataState];

export const DEFAULT_CUSTOMER_DATA_STATE: CustomerDataStateObject = {
  isRemote: false,
  identifier: null,
  gender: null,
  salutation: null,
  honorificPrefix: null,
  givenName: null,
  alternateGivenName1: null,
  alternateGivenName2: null,
  familyName: null,
  alternateFamilyName1: null,
  alternateFamilyName2: null,
  birthDate: null,
  nationality: null,
  eReceipt: false,
  email: null,
  telephone: null,
  mobileTelephone: null,
  wechatId: null,
  lineId: null,
  streetAddress1: null,
  streetAddress2: null,
  streetAddress3: null,
  addressLocality: null,
  addressRegion: null,
  postalCode: null,
  addressCountry: null,
  appsConsent: false,
  emailConsent: false,
  postConsent: false,
  phoneConsent: false,
  salesPerson: null,
  creationStore: null,
  preferredLanguage: null,
  notes: null,
  preferencesCategories: [],
  preferencesContact: [],
  isEmailValid: null,
  isAddressValid: null,
  isMobilePhoneValid: null,
  katakanaFirstname: null,
  katakanaLastname: null
};

export let customerDataState: Partial<CustomerDataStateObject> = {};

export function setIsRemote(isRemote: boolean) {
  DEFAULT_CUSTOMER_DATA_STATE.isRemote = isRemote;
}

export function setCustomerDataState(
  customer: Partial<CustomerDataStateObject>
) {
  customerDataState = customer;
}

export default function useCustomerDataState(
  initialState?: Partial<CustomerDataStateObject>
): CustomerDataState {
  return useState<CustomerDataStateObject>({
    ...DEFAULT_CUSTOMER_DATA_STATE,
    ...customerDataState,
    ...initialState
  });
}
